import React, { useState, useEffect } from 'react';
import Latex from 'react-latex-next';
import { renderContent } from '../renderContent';
import { useBandeau } from '../BandeauContext';
import { apiClient } from '../../utils/apiClient';

// Repair dollar-sign delimiters after splitting a LaTeX string by [_] placeholders.
// e.g. "$[_] + [_]$" splits to ["$", " + ", "$"] — each fragment gets balanced $ signs.
function repairLatexParts(parts) {
    let dollarsBefore = 0;
    return parts.map(part => {
        let healed = part;
        if (dollarsBefore % 2 === 1) healed = '$' + healed;
        const count = (healed.match(/\$/g) || []).length;
        if (count % 2 === 1) healed += '$';
        dollarsBefore += (part.match(/\$/g) || []).length;
        return healed;
    });
}

function QuizSession({ quizzes, user, onExit, preview = false }) {
    const [sessionQuizzes, setSessionQuizzes] = useState([]);
    const [sessionKey, setSessionKey] = useState(0);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showQuizResult, setShowQuizResult] = useState(false);
    const [isHintShown, setIsHintShown] = useState(false);
    const [matchingSelection, setMatchingSelection] = useState({});
    const [activeLeft, setActiveLeft] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [textAnswer, setTextAnswer] = useState('');
    const [questionStartTime, setQuestionStartTime] = useState(null);
    const [isReporting, setIsReporting] = useState(false);
    const [reportText, setReportText] = useState('');
    const [canSkip, setCanSkip] = useState(false);
    const [notification, setNotification] = useState(null);
    const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0 });
    const [orderingSelection, setOrderingSelection] = useState([]);
    const [fillInBlanks, setFillInBlanks] = useState([]);
    const [activeBlankIndex, setActiveBlankIndex] = useState(0);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [draggedLabel, setDraggedLabel] = useState(null);
    const [draggedFromBlankIndex, setDraggedFromBlankIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const { setBandeau, resetBandeau } = useBandeau();

    useEffect(() => {
        if (preview) return;
        setBandeau({ title: 'Quiz', breadcrumb: [{ label: 'Accueil', onClick: onExit }] });
        return () => resetBandeau();
    }, [preview, setBandeau, resetBandeau, onExit]);

    useEffect(() => {
        const parsedQuizzes = quizzes.map(quiz => {
            const opts = typeof quiz.answer === 'string' ? JSON.parse(quiz.answer) : quiz.answer;
            const quizData = Array.isArray(opts) ? { type: 'single_choice', options: opts } : opts;
            if (quizData.type === 'single_choice' || quizData.type === 'multiple_choice') {
                let shuffledOpts = [...quizData.options];
                for (let i = shuffledOpts.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffledOpts[i], shuffledOpts[j]] = [shuffledOpts[j], shuffledOpts[i]];
                }
                return { ...quiz, ...quizData, options: shuffledOpts };
            } else if (quizData.type === 'matching') {
                let rights = quizData.pairs.map(p => p.right);
                for (let i = rights.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [rights[i], rights[j]] = [rights[j], rights[i]];
                }
                return { ...quiz, ...quizData, shuffledRights: rights };
            } else if (quizData.type === 'ordering') {
                let shuffledItems = [...quizData.items];
                for (let i = shuffledItems.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffledItems[i], shuffledItems[j]] = [shuffledItems[j], shuffledItems[i]];
                }
                return { ...quiz, ...quizData, shuffledItems };
            } else if (quizData.type === 'fill_in_the_blank') {
                let labels = [...quizData.blanks, ...(quizData.distractors || [])].map((text, i) => ({ id: i, text }));
                for (let i = labels.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [labels[i], labels[j]] = [labels[j], labels[i]];
                }
                return { ...quiz, ...quizData, shuffledLabels: labels };
            }
            return { ...quiz, ...quizData };
        });
        setSessionQuizzes(parsedQuizzes);
        setCurrentQuizIndex(0);
        setQuestionStartTime(Date.now());
    }, [quizzes, sessionKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setCanSkip(false);
        const timer = setTimeout(() => setCanSkip(true), 10000);
        return () => clearTimeout(timer);
    }, [currentQuizIndex]);

    useEffect(() => {
        if (sessionQuizzes.length > 0) {
            const currentQuiz = sessionQuizzes[currentQuizIndex];
            if (currentQuiz.type === 'ordering') {
                setOrderingSelection([...currentQuiz.shuffledItems]);
            } else if (currentQuiz.type === 'fill_in_the_blank') {
                setFillInBlanks(new Array(currentQuiz.blanks.length).fill(null));
                setActiveBlankIndex(0);
            }
        }
    }, [currentQuizIndex, sessionQuizzes]);

    const handleQuizOptionClick = (option) => {
        if (showQuizResult) return;
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        if (currentQuiz.type === 'multiple_choice') {
            if (selectedOptions.includes(option)) setSelectedOptions(selectedOptions.filter(o => o !== option));
            else setSelectedOptions([...selectedOptions, option]);
            return;
        }
        setSelectedOption(option);
    };

    const moveOrderingItem = (idx, dir) => {
        if (showQuizResult) return;
        const newOrdering = [...orderingSelection];
        if (dir === 'up' && idx > 0) {
            [newOrdering[idx - 1], newOrdering[idx]] = [newOrdering[idx], newOrdering[idx - 1]];
        } else if (dir === 'down' && idx < newOrdering.length - 1) {
            [newOrdering[idx + 1], newOrdering[idx]] = [newOrdering[idx], newOrdering[idx + 1]];
        }
        setOrderingSelection(newOrdering);
    };

    const handleRemoveBlank = (idx) => {
        if (showQuizResult) return;
        const newBlanks = [...fillInBlanks];
        newBlanks[idx] = null;
        setFillInBlanks(newBlanks);
        setActiveBlankIndex(idx);
    };

    const handleAddLabelToBlank = (labelObj) => {
        if (showQuizResult) return;
        let targetIdx = activeBlankIndex;
        if (fillInBlanks[targetIdx] !== null) {
            targetIdx = fillInBlanks.findIndex(b => b === null);
        }
        if (targetIdx !== -1) {
            const newBlanks = [...fillInBlanks];
            newBlanks[targetIdx] = labelObj;
            setFillInBlanks(newBlanks);
            const nextEmpty = newBlanks.findIndex(b => b === null);
            setActiveBlankIndex(nextEmpty !== -1 ? nextEmpty : 0);
        }
    };

    const handleOrderingDragStart = (e, index) => {
        if (showQuizResult) return;
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleOrderingDragOver = (e, index) => {
        if (showQuizResult) return;
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleOrderingDrop = (e, targetIndex) => {
        if (showQuizResult) return;
        e.preventDefault();
        setDragOverIndex(null);
        if (draggedItemIndex === null || draggedItemIndex === targetIndex) return;
        const newOrdering = [...orderingSelection];
        const itemToMove = newOrdering[draggedItemIndex];
        newOrdering.splice(draggedItemIndex, 1);
        newOrdering.splice(targetIndex, 0, itemToMove);
        setOrderingSelection(newOrdering);
        setDraggedItemIndex(null);
    };

    const handleLabelDragStart = (e, labelObj, fromBlankIndex = null) => {
        if (showQuizResult) return;
        setDraggedLabel(labelObj);
        setDraggedFromBlankIndex(fromBlankIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleBlankDragOver = (e, blankIndex) => {
        if (showQuizResult) return;
        e.preventDefault();
        setDragOverIndex(blankIndex);
    };

    const handleBlankDrop = (e, targetBlankIndex) => {
        if (showQuizResult) return;
        e.preventDefault();
        setDragOverIndex(null);
        if (!draggedLabel) return;
        const newBlanks = [...fillInBlanks];
        const existingLabelInTarget = newBlanks[targetBlankIndex];
        if (draggedFromBlankIndex !== null) newBlanks[draggedFromBlankIndex] = existingLabelInTarget;
        else {
            const previousIndex = newBlanks.findIndex(b => b && b.id === draggedLabel.id);
            if (previousIndex !== -1) newBlanks[previousIndex] = existingLabelInTarget;
        }
        newBlanks[targetBlankIndex] = draggedLabel;
        setFillInBlanks(newBlanks);
        const nextEmpty = newBlanks.findIndex(b => b === null);
        setActiveBlankIndex(nextEmpty !== -1 ? nextEmpty : targetBlankIndex);
        setDraggedLabel(null);
        setDraggedFromBlankIndex(null);
    };

    const sendReview = (niveau, responseData, timeSpent, currentQuizId) => {
        if (preview) return;
        apiClient(`${process.env.REACT_APP_API_BASE_URL}/save_quiz_review.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ student_id: user.id, quiz_id: currentQuizId, rating: niveau, response_data: responseData, time_spent: timeSpent })
        }).then(async res => {
            const text = await res.text();
            try { return JSON.parse(text); } catch(e) { throw new Error("Erreur PHP : " + text); }
        }).then(data => {
            if (!data.success) {
                setNotification({ type: 'error', text: "Erreur : " + data.message });
                setTimeout(() => setNotification(null), 4000);
            }
        }).catch(err => {
            setNotification({ type: 'error', text: "Erreur de connexion : " + err.message });
            setTimeout(() => setNotification(null), 4000);
        });
    };

    const handleSingleChoiceSubmit = () => {
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        setShowQuizResult(true);
        const selectedObj = currentQuiz.options.find(o => (o.text || o) === selectedOption);
        const isCorrect = selectedObj && selectedObj.isCorrect;
        setSessionStats(prev => ({ ...prev, [isCorrect ? 'correct' : 'incorrect']: prev[isCorrect ? 'correct' : 'incorrect'] + 1 }));
        sendReview(isCorrect ? (isHintShown ? 1 : 2) : 0, { answer: selectedOption, options_order: currentQuiz.options.map(o => o.text || o) }, Math.round((Date.now() - questionStartTime) / 1000), currentQuiz.id);
    };

    const handleMultipleChoiceSubmit = () => {
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        setShowQuizResult(true);
        const correctOptions = currentQuiz.options.filter(o => o.isCorrect).map(o => o.text || o);
        const isCorrect = correctOptions.length === selectedOptions.length && correctOptions.every(o => selectedOptions.includes(o));
        setSessionStats(prev => ({ ...prev, [isCorrect ? 'correct' : 'incorrect']: prev[isCorrect ? 'correct' : 'incorrect'] + 1 }));
        sendReview(isCorrect ? (isHintShown ? 1 : 2) : 0, { answer: selectedOptions, options_order: currentQuiz.options.map(o => o.text || o) }, Math.round((Date.now() - questionStartTime) / 1000), currentQuiz.id);
    };

    const handleMatchingSubmit = () => {
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        setShowQuizResult(true);
        let isCorrect = true;
        currentQuiz.pairs.forEach(p => { if (matchingSelection[p.left] !== p.right) isCorrect = false; });
        setSessionStats(prev => ({ ...prev, [isCorrect ? 'correct' : 'incorrect']: prev[isCorrect ? 'correct' : 'incorrect'] + 1 }));
        sendReview(isCorrect ? (isHintShown ? 1 : 2) : 0, { answer: matchingSelection, options_order: { left: currentQuiz.pairs.map(p => p.left), right: currentQuiz.shuffledRights } }, Math.round((Date.now() - questionStartTime) / 1000), currentQuiz.id);
    };

    const handleTextSubmit = () => {
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        setShowQuizResult(true);
        const normalize = (str) => (str || '').toString().toLowerCase().replace(/[\s$]/g, '');
        const matchedOption = (currentQuiz.options || []).find(o => normalize(o.text || o) === normalize(textAnswer));
        const isCorrect = Boolean(matchedOption && matchedOption.isCorrect);
        setSessionStats(prev => ({ ...prev, [isCorrect ? 'correct' : 'incorrect']: prev[isCorrect ? 'correct' : 'incorrect'] + 1 }));
        sendReview(isCorrect ? (isHintShown ? 1 : 2) : 0, { answer: textAnswer, options_order: null }, Math.round((Date.now() - questionStartTime) / 1000), currentQuiz.id);
    };

    const handleOrderingSubmit = () => {
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        setShowQuizResult(true);
        const isCorrect = JSON.stringify(orderingSelection) === JSON.stringify(currentQuiz.items);
        setSessionStats(prev => ({ ...prev, [isCorrect ? 'correct' : 'incorrect']: prev[isCorrect ? 'correct' : 'incorrect'] + 1 }));
        sendReview(isCorrect ? (isHintShown ? 1 : 2) : 0, { answer: orderingSelection, options_order: currentQuiz.items }, Math.round((Date.now() - questionStartTime) / 1000), currentQuiz.id);
    };

    const handleFillInBlankSubmit = () => {
        const currentQuiz = sessionQuizzes[currentQuizIndex];
        setShowQuizResult(true);
        let isCorrect = true;
        for (let i = 0; i < currentQuiz.blanks.length; i++) {
            if (!fillInBlanks[i] || fillInBlanks[i].text !== currentQuiz.blanks[i]) isCorrect = false;
        }
        setSessionStats(prev => ({ ...prev, [isCorrect ? 'correct' : 'incorrect']: prev[isCorrect ? 'correct' : 'incorrect'] + 1 }));
        sendReview(isCorrect ? (isHintShown ? 1 : 2) : 0, { answer: fillInBlanks.map(b => b ? b.text : null), options_order: currentQuiz.blanks }, Math.round((Date.now() - questionStartTime) / 1000), currentQuiz.id);
    };

    const submitReport = () => {
        if (reportText.trim() === '') return;
        apiClient(`${process.env.REACT_APP_API_BASE_URL}/report_quiz.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ student_id: user.id, quiz_id: sessionQuizzes[currentQuizIndex].id, report: reportText })
        }).then(() => {
            setNotification({ type: 'success', text: "Signalement envoyé." });
            setIsReporting(false);
        }).catch(() => setNotification({ type: 'error', text: "Erreur d'envoi." }));
    };

    const nextQuiz = () => {
        if (currentQuizIndex + 1 < sessionQuizzes.length) {
            setShowQuizResult(false);
            setSelectedOption(null);
            setIsHintShown(false);
            setMatchingSelection({});
            setActiveLeft(null);
            setSelectedOptions([]);
            setTextAnswer('');
            setQuestionStartTime(Date.now());
            setIsReporting(false);
            setOrderingSelection([]);
            setFillInBlanks([]);
            setActiveBlankIndex(0);
            setDraggedItemIndex(null);
            setDraggedLabel(null);
            setDraggedFromBlankIndex(null);
            setDragOverIndex(null);
            setReportText('');
            setCurrentQuizIndex(currentQuizIndex + 1);
        } else if (preview) {
            setShowQuizResult(false);
            setSelectedOption(null);
            setSelectedOptions([]);
            setIsHintShown(false);
            setMatchingSelection({});
            setActiveLeft(null);
            setTextAnswer('');
            setIsReporting(false);
            setReportText('');
            setDraggedItemIndex(null);
            setDraggedLabel(null);
            setDraggedFromBlankIndex(null);
            setDragOverIndex(null);
            setSessionStats({ correct: 0, incorrect: 0 });
            setSessionKey(k => k + 1); // re-parse + re-shuffle + reset index via useEffect
        } else {
            setNotification({ type: 'success', text: "Session terminée !" });
            setTimeout(() => onExit(sessionStats), 2500);
        }
    };

    if (sessionQuizzes.length === 0) return <div className="text-gray-800 dark:text-white text-center mt-10">Chargement...</div>;

    const currentQuiz = sessionQuizzes[currentQuizIndex];

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in relative z-10 text-gray-800 dark:text-gray-200">
            {!preview && (
                <div className="w-full max-w-2xl mb-8">
                    <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 font-semibold mb-2"><span>Progression</span><span>Question {currentQuizIndex + 1} sur {sessionQuizzes.length}</span></div>
                    <div className="w-full bg-gray-200 dark:bg-white/[0.05] rounded-full h-2 overflow-hidden"><div className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-2 rounded-full transition-all duration-500 ease-out" style={{ width: `${((currentQuizIndex + 1) / sessionQuizzes.length) * 100}%` }}></div></div>
                </div>
            )}
            <div className="w-full max-w-2xl bg-white/70 dark:bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-10 border border-gray-200 dark:border-white/[0.08] shadow-[0_0_50px_rgba(16,185,129,0.1)] relative overflow-hidden mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 pointer-events-none"></div>
                <p className="text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-bold mb-6 text-center z-10">
                    {currentQuiz.category && currentQuiz.category.split('>').map((cat, i, arr) => (<React.Fragment key={i}><span>{cat.trim()}</span>{i < arr.length - 1 && <span className="mx-2 opacity-50">❯</span>}</React.Fragment>))}
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white text-center leading-relaxed z-10 w-full">{renderContent(currentQuiz.question)}</h2>
            </div>
            {currentQuiz.type === 'matching' ? (
                <div className="w-full mb-8 max-w-4xl relative z-10 flex flex-col gap-8">
                    {Object.keys(matchingSelection).length > 0 && (
                        <div className="w-full flex flex-col gap-3">
                            {Object.entries(matchingSelection).map(([left, right], idx) => {
                                let borderClass = showQuizResult ? ((currentQuiz.pairs.find(p => p.left === left)?.right === right) ? "border-emerald-500/50 bg-emerald-500/20" : "border-red-500/50 bg-red-500/20") : "border-cyan-500/50 bg-cyan-500/10 hover:bg-red-500/20 hover:border-red-500/50 cursor-pointer";
                                return (
                                    <div key={idx} className="flex flex-col animate-fade-in">
                                    <button disabled={showQuizResult} onClick={() => { const newSel = { ...matchingSelection }; delete newSel[left]; setMatchingSelection(newSel); }} className={`flex flex-col p-4 rounded-xl transition-all border w-full ${borderClass}`}>
                                        <div className="flex flex-col md:flex-row items-center justify-between w-full">
                                            <div className="text-gray-800 dark:text-gray-200 font-medium flex-1 text-center md:text-left">{renderContent(left)}</div>
                                            <div className="text-cyan-700 dark:text-cyan-300 font-medium flex-1 text-center md:text-right">{renderContent(right)}</div>
                                        </div>
                                        {showQuizResult && (currentQuiz.pairs.find(p => p.left === left)?.right !== right) && (
                                            <div className="mt-3 pt-3 border-t border-red-500/30 w-full text-sm flex flex-wrap items-center justify-center gap-2">
                                                <span className="font-semibold text-red-800 dark:text-red-200">Correction :</span>
                                                <div className="font-bold bg-emerald-500/20 text-emerald-800 dark:text-emerald-200 px-3 py-1 rounded-lg">{renderContent(currentQuiz.pairs.find(p => p.left === left)?.right)}</div>
                                            </div>
                                        )}
                                    </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {!showQuizResult && Object.keys(matchingSelection).length < (currentQuiz.pairs || []).length && (
                        <div className="w-full flex flex-col md:flex-row gap-8">
                            <div className="flex-1 flex flex-col gap-3">
                                {(currentQuiz.pairs || []).filter(p => !matchingSelection[p.left]).map((p, idx) => (
                                    <button key={idx} onClick={() => setActiveLeft(activeLeft === p.left ? null : p.left)} className={`p-4 rounded-xl text-center transition-all border ${activeLeft === p.left ? "border-cyan-500 bg-cyan-500/20" : "border-gray-200 dark:border-white/[0.1] bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 dark:hover:bg-white/[0.1]"} animate-fade-in`}><div className="text-gray-800 dark:text-gray-200 font-medium">{renderContent(p.left)}</div></button>
                                ))}
                            </div>
                            <div className="flex-1 flex flex-col gap-3">
                                {(currentQuiz.shuffledRights || []).filter(r => !Object.values(matchingSelection).includes(r)).map((r, idx) => (
                                    <button key={idx} disabled={!activeLeft} onClick={() => { if (activeLeft) { setMatchingSelection({ ...matchingSelection, [activeLeft]: r }); setActiveLeft(null); } }} className={`p-4 rounded-xl text-center transition-all border ${activeLeft ? "border-cyan-500/50 hover:bg-cyan-500/10 cursor-pointer" : "border-gray-200 dark:border-white/[0.1] bg-gray-100 dark:bg-white/[0.05] opacity-50 cursor-not-allowed"} animate-fade-in`}><div className="text-gray-800 dark:text-gray-200 font-medium">{renderContent(r)}</div></button>
                                ))}
                            </div>
                        </div>
                    )}
                    {!showQuizResult && <button onClick={handleMatchingSubmit} className={`mt-4 w-full max-w-2xl mx-auto py-4 rounded-2xl font-bold text-white transition-all ${(Object.keys(matchingSelection).length === (currentQuiz.pairs || []).length) ? 'bg-cyan-500/20 border border-cyan-500/50' : 'bg-gray-500/20 border border-gray-500/50'}`}>{(Object.keys(matchingSelection).length === (currentQuiz.pairs || []).length) ? 'Valider mes associations' : 'Passer la question'}</button>}
                </div>
            ) : currentQuiz.type === 'ordering' ? (
                <div className="w-full mb-8 max-w-2xl flex flex-col relative z-10 gap-3">
                    {orderingSelection.map((item, idx) => {
                        const isCorrectPosition = showQuizResult ? item === currentQuiz.items[idx] : null;
                        const isDraggingOver = dragOverIndex === idx;
                        const isBeingDragged = draggedItemIndex === idx;
                        let borderClass = showQuizResult ? (isCorrectPosition ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200" : "border-red-500/50 bg-red-500/10 text-red-800 dark:text-red-200") : "border-gray-300 dark:border-white/[0.1] bg-white dark:bg-white/[0.05] text-gray-800 dark:text-white";
                        if (!showQuizResult && isDraggingOver) borderClass = "border-cyan-500 bg-cyan-500/10 text-cyan-800 dark:text-cyan-200 scale-[1.02] shadow-md z-10";
                        if (!showQuizResult && isBeingDragged) borderClass += " opacity-50";
                        return (
                            <div 
                                key={idx} 
                                draggable={!showQuizResult}
                                onDragStart={(e) => handleOrderingDragStart(e, idx)}
                                onDragOver={(e) => handleOrderingDragOver(e, idx)}
                                onDrop={(e) => handleOrderingDrop(e, idx)}
                                onDragEnd={() => { setDragOverIndex(null); setDraggedItemIndex(null); }}
                                className={`flex items-center justify-between p-4 rounded-xl border transition-all ${!showQuizResult ? 'cursor-grab active:cursor-grabbing' : ''} ${borderClass}`}
                            >
                                <div className="font-medium px-4 flex items-center gap-4 pointer-events-none select-none">
                                    <span className="text-gray-400 font-bold">{idx + 1}.</span> 
                                    {renderContent(item)}
                                </div>
                                {!showQuizResult && (
                                    <div className="flex flex-col gap-1">
                                        <button onClick={() => moveOrderingItem(idx, 'up')} disabled={idx === 0} className={`p-1 rounded hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 ${idx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}>▲</button>
                                        <button onClick={() => moveOrderingItem(idx, 'down')} disabled={idx === orderingSelection.length - 1} className={`p-1 rounded hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 ${idx === orderingSelection.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}>▼</button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {showQuizResult && JSON.stringify(orderingSelection) !== JSON.stringify(currentQuiz.items) && (
                        <div className="mt-4 p-5 rounded-2xl border bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-200 flex flex-col gap-2">
                            <span className="font-semibold mb-2">L'ordre correct était :</span>
                            <div className="flex flex-col gap-2">
                                {currentQuiz.items.map((item, idx) => (
                                    <div key={`corr-${idx}`} className="px-4 py-2 border border-emerald-500/20 bg-emerald-500/10 rounded-lg flex items-center gap-3">
                                        <span className="font-bold opacity-70">{idx + 1}.</span> {renderContent(item)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {!showQuizResult && <button onClick={handleOrderingSubmit} className="mt-6 w-full max-w-2xl mx-auto py-4 rounded-2xl font-bold text-cyan-700 dark:text-white bg-cyan-500/20 border border-cyan-500/50 hover:bg-cyan-500/30 transition-all">Valider l'ordre</button>}
                </div>
            ) : currentQuiz.type === 'fill_in_the_blank' ? (
                <div className="w-full mb-8 max-w-3xl flex flex-col relative z-10 gap-6">
                    <div className="bg-gray-100 dark:bg-white/[0.05] p-6 rounded-2xl border border-gray-200 dark:border-white/[0.1] text-lg md:text-xl leading-[2.5] text-center text-gray-800 dark:text-gray-200 shadow-inner">
                        {repairLatexParts(currentQuiz.text.split('[_]')).map((part, idx, arr) => (
                            <React.Fragment key={idx}>
                                <span className="inline-flex items-center align-middle">{part === '$$' ? null : renderContent(part)}</span>
                                {idx < arr.length - 1 && (() => {
                                    const isDragTarget = dragOverIndex === idx && !showQuizResult;
                                    return (
                                    <span className="inline-flex items-center align-middle relative">
                                        <span 
                                            draggable={!showQuizResult && fillInBlanks[idx] !== null}
                                            onDragStart={(e) => handleLabelDragStart(e, fillInBlanks[idx], idx)}
                                            onDragOver={(e) => handleBlankDragOver(e, idx)}
                                            onDrop={(e) => handleBlankDrop(e, idx)}
                                            onDragEnd={() => { setDragOverIndex(null); setDraggedLabel(null); setDraggedFromBlankIndex(null); }}
                                            onClick={() => {
                                                if (showQuizResult) return;
                                                if (fillInBlanks[idx]) handleRemoveBlank(idx);
                                                else setActiveBlankIndex(idx);
                                            }}
                                            className={`inline-flex items-center justify-center min-w-[100px] text-center mx-2 px-3 py-1 border-b-4 font-bold transition-all min-h-[2.5rem] outline-none select-none
                                            ${fillInBlanks[idx] ? 
                                                (showQuizResult ? (fillInBlanks[idx].text === currentQuiz.blanks[idx] ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300 bg-emerald-500/20' : 'border-red-500 text-red-700 dark:text-red-300 bg-red-500/20 line-through opacity-80') : 'border-cyan-500 text-cyan-800 dark:text-cyan-200 bg-cyan-500/20 hover:bg-red-500/20 hover:border-red-500 hover:text-red-700 hover:line-through shadow-sm cursor-grab active:cursor-grabbing') 
                                                : (isDragTarget ? 'border-cyan-500 bg-cyan-500/20 text-cyan-700 scale-110 shadow-lg' : (idx === activeBlankIndex && !showQuizResult ? 'border-cyan-500 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 scale-105 shadow-[0_4px_15px_rgba(6,182,212,0.25)] ring-2 ring-cyan-500/40 rounded-t-lg z-10 cursor-pointer' : 'border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-black/20 text-gray-400 hover:border-cyan-400 hover:bg-cyan-500/5 cursor-pointer'))}`}
                                        >
                                            {fillInBlanks[idx] ? <span className="pointer-events-none">{renderContent(fillInBlanks[idx].text)}</span> : '...'}
                                        </span>
                                        {showQuizResult && fillInBlanks[idx]?.text !== currentQuiz.blanks[idx] && (
                                            <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-emerald-500 text-white font-bold border border-emerald-600 rounded-lg text-xs shadow-md z-20">
                                                {renderContent(currentQuiz.blanks[idx])}
                                            </span>
                                        )}
                                    </span>
                                );})()}
                            </React.Fragment>
                        ))}
                    </div>
                    {!showQuizResult && (
                        <div className="flex flex-wrap justify-center gap-3 mt-4">
                            {currentQuiz.shuffledLabels.map((labelObj, idx) => {
                                const isUsed = fillInBlanks.some(b => b !== null && b.id === labelObj.id);
                                const isBeingDragged = draggedLabel && draggedLabel.id === labelObj.id && draggedFromBlankIndex === null;
                                return (
                                    <button 
                                        key={idx} 
                                        draggable={!showQuizResult && !isUsed}
                                        onDragStart={(e) => handleLabelDragStart(e, labelObj, null)}
                                        onDragEnd={() => { setDragOverIndex(null); setDraggedLabel(null); setDraggedFromBlankIndex(null); }}
                                        onClick={() => handleAddLabelToBlank(labelObj)} 
                                        disabled={isUsed} 
                                        className={`px-5 py-2.5 rounded-xl border-2 transition-all shadow-sm font-bold text-sm md:text-base select-none ${isBeingDragged ? 'opacity-50 border-cyan-500 scale-95' : ''} ${isUsed ? 'opacity-30 border-gray-300 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 cursor-not-allowed text-gray-500' : 'border-cyan-500/50 bg-white dark:bg-gray-800 hover:bg-cyan-500/10 hover:border-cyan-500 hover:-translate-y-1 text-cyan-800 dark:text-cyan-200 cursor-grab active:cursor-grabbing'}`}
                                    >
                                        <span className="pointer-events-none">{renderContent(labelObj.text)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {!showQuizResult && fillInBlanks.every(b => b !== null) && (
                        <button onClick={handleFillInBlankSubmit} className="mt-4 w-full max-w-2xl mx-auto py-4 rounded-2xl font-bold text-cyan-700 dark:text-white bg-cyan-500/20 border border-cyan-500/50 hover:bg-cyan-500/30 transition-all">Valider ma réponse</button>
                    )}
                </div>
            ) : currentQuiz.type === 'short_text' || currentQuiz.type === 'numeric' ? (
                <div className="w-full mb-8 max-w-2xl flex flex-col relative z-10">
                    <div className="relative w-full">
                        {currentQuiz.type === 'numeric' ? (
                            <input type="number" step="any" value={textAnswer} onChange={(e) => setTextAnswer(e.target.value)} disabled={showQuizResult} className={`w-full bg-gray-100 dark:bg-black/40 border-2 rounded-2xl p-6 pr-14 text-xl md:text-2xl text-center outline-none transition-all ${showQuizResult ? ((currentQuiz.options.find(o => (o.text || o).toString().toLowerCase().replace(/[\s$]/g, '') === textAnswer.toString().toLowerCase().replace(/[\s$]/g, ''))?.isCorrect) ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-300') : 'border-gray-300 dark:border-white/[0.1] focus:border-cyan-500 text-gray-800 dark:text-white'}`} placeholder="Saisis ta réponse" onKeyPress={(e) => e.key === 'Enter' && !showQuizResult && textAnswer.trim() !== '' && handleTextSubmit()} />
                        ) : (
                            <input type="text" value={textAnswer} onChange={(e) => setTextAnswer(e.target.value)} disabled={showQuizResult} className={`w-full bg-gray-100 dark:bg-black/40 border-2 rounded-2xl p-6 pr-14 text-xl md:text-2xl text-center outline-none transition-all ${showQuizResult ? ((currentQuiz.options.find(o => (o.text || o).toString().toLowerCase().replace(/[\s$]/g, '') === textAnswer.toString().toLowerCase().replace(/[\s$]/g, ''))?.isCorrect) ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-300') : 'border-gray-300 dark:border-white/[0.1] focus:border-cyan-500 text-gray-800 dark:text-white'}`} placeholder={currentQuiz.isFormula ? "Saisis ta formule" : "Saisis ta réponse"} onKeyPress={(e) => e.key === 'Enter' && !showQuizResult && textAnswer.trim() !== '' && handleTextSubmit()} />
                        )}
                        {currentQuiz.isFormula && !showQuizResult && (<div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex justify-center"><div className="relative group cursor-pointer"><span className="text-cyan-600/40 hover:text-cyan-500 dark:text-cyan-400/40 dark:hover:text-cyan-300 w-8 h-8 rounded-full flex items-center justify-center font-bold font-serif transition-all">i</span><div className="absolute bottom-full right-[-10px] mb-3 w-64 p-4 bg-gray-800 dark:bg-slate-800 border border-gray-700 dark:border-white/[0.1] rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none"><strong className="text-cyan-600 dark:text-cyan-400 block mb-3 text-sm border-b pb-2 border-gray-700">Commandes utiles</strong><div className="flex flex-col gap-3 text-xs text-gray-300 font-mono"><div className="flex justify-between items-center"><span>Fraction</span> <code>{"\\frac{a}{b}"}</code></div><div className="flex justify-between items-center"><span>Puissance</span> <code>{"x^2"}</code></div><div className="flex justify-between items-center"><span>Racine</span> <code>{"\\sqrt{x}"}</code></div></div><div className="absolute top-full right-[18px] border-8 border-transparent border-t-gray-800 dark:border-t-slate-800"></div></div></div></div>)}
                    </div>
                    {!showQuizResult && textAnswer.trim() !== '' && currentQuiz.isFormula && <div className="mt-4 p-4 bg-gray-100 dark:bg-white/[0.02] border border-gray-300 dark:border-white/[0.05] rounded-xl text-center"><span className="text-xs text-gray-500 uppercase tracking-widest block mb-2">Aperçu</span><div className="text-xl text-cyan-800 dark:text-cyan-200"><Latex>{textAnswer.includes('$') ? textAnswer : `$${textAnswer}$`}</Latex></div></div>}
                    {showQuizResult && (() => {
                        const normalize = (str) => (str || '').toString().toLowerCase().replace(/[\s$]/g, '');
                        const matchedOption = (currentQuiz.options || []).find(o => normalize(o.text || o) === normalize(textAnswer));
                        const isCorrect = Boolean(matchedOption && matchedOption.isCorrect);
                        const hasMatchedFeedback = matchedOption && matchedOption.feedback;
                        const correctOptions = (currentQuiz.options || []).filter(o => o.isCorrect);

                        if (isCorrect && !hasMatchedFeedback) return null;
                        if (!isCorrect && !hasMatchedFeedback && correctOptions.length === 0) return null;

                        return (
                            <div className="mt-8 space-y-4 animate-fade-in text-left">
                                {hasMatchedFeedback && (
                                    <div className={`p-5 rounded-2xl border ${isCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-200' : 'bg-red-500/10 border-red-500/30 text-red-800 dark:text-red-200'}`}>
                                        {renderContent(matchedOption.feedback)}
                                    </div>
                                )}
                                {!isCorrect && correctOptions.length > 0 && (
                                    <div className="p-5 rounded-2xl border bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-200 flex flex-col gap-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-semibold">Correction :</span>
                                            <div className="font-bold bg-emerald-500/20 px-3 py-1 rounded-lg">{renderContent(correctOptions[0].text || correctOptions[0])}</div>
                                        </div>
                                        {correctOptions[0].feedback && (
                                            <div className="opacity-90 mt-1">{renderContent(correctOptions[0].feedback)}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    {!showQuizResult && textAnswer.trim() !== '' && <button onClick={handleTextSubmit} className="mt-6 w-full max-w-2xl mx-auto py-4 rounded-2xl font-bold text-cyan-700 dark:text-white bg-cyan-500/20 border border-cyan-500/50 hover:bg-cyan-500/30 transition-all">Valider ma réponse</button>}
                </div>
            ) : (
                <div className="w-full mb-8 max-w-2xl flex flex-col relative z-10">
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(currentQuiz.options || []).map((optionObj, idx) => {
                            const optionText = optionObj.text || optionObj;
                            const isOptionCorrect = optionObj.isCorrect;
                            let isSelected = currentQuiz.type === 'multiple_choice' ? selectedOptions.includes(optionText) : optionText === selectedOption; // Fix: use optionText for comparison
                            let buttonClass = showQuizResult ? (isOptionCorrect && isSelected ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-700 dark:text-emerald-400" : isOptionCorrect ? "bg-blue-500/20 border-blue-500/50 text-blue-700 dark:text-blue-400" : isSelected ? "bg-red-500/20 border-red-500/50 text-red-700 dark:text-red-400" : "bg-gray-100 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.02] text-gray-500 opacity-50") : isSelected ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-700 dark:text-cyan-200" : "bg-gray-100 dark:bg-white/[0.05] border-gray-200 dark:border-white/[0.1] hover:bg-gray-200 dark:hover:bg-white/[0.1] text-gray-800 dark:text-gray-200";
                            return (
                                <button key={idx} onClick={() => handleQuizOptionClick(optionText)} disabled={showQuizResult} className={`p-4 rounded-xl border transition-all w-full h-full flex flex-col items-center justify-center gap-2 ${buttonClass}`}>
                                    <div className="font-medium">{renderContent(optionText)}</div>
                                    {showQuizResult && (isSelected || isOptionCorrect) && optionObj.feedback && (
                                        <div className="text-sm opacity-90 mt-1">{renderContent(optionObj.feedback)}</div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {!showQuizResult && ((currentQuiz.type === 'multiple_choice' ? selectedOptions.length > 0 : !!selectedOption) || canSkip) && (
                        <button onClick={currentQuiz.type === 'multiple_choice' ? handleMultipleChoiceSubmit : handleSingleChoiceSubmit} className={`mt-6 w-full max-w-2xl mx-auto py-4 rounded-2xl font-bold transition-all ${(currentQuiz.type === 'multiple_choice' ? selectedOptions.length > 0 : !!selectedOption) ? 'text-cyan-700 dark:text-white bg-cyan-500/20 border border-cyan-500/50' : 'text-gray-500 dark:text-white bg-gray-200 dark:bg-gray-500/20 border border-gray-300 dark:border-gray-500/50'}`}>{(currentQuiz.type === 'multiple_choice' ? selectedOptions.length > 0 : !!selectedOption) ? `Valider ${currentQuiz.type === 'multiple_choice' ? 'mes réponses' : 'ma réponse'}` : 'Passer la question'}</button>
                    )}
                </div>
            )}
            {!showQuizResult && currentQuiz.hint && !isHintShown && <button onClick={() => setIsHintShown(true)} className="mb-6 px-5 py-2.5 rounded-full border border-yellow-500/50 text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20 text-sm font-semibold">💡 Voir un indice</button>}
            {isHintShown && currentQuiz.hint && <div className="mb-8 w-full max-w-2xl p-5 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl text-yellow-800 dark:text-yellow-200 text-sm"><strong className="text-yellow-600 dark:text-yellow-400">💡 Indice :</strong> {renderContent(currentQuiz.hint)}</div>}
            {showQuizResult && (
                <div className="space-y-4 w-full max-w-2xl animate-fade-in relative z-10">
                    {currentQuiz.feedback && <div className="p-5 rounded-2xl border bg-blue-500/10 border-blue-500/30 text-blue-800 dark:text-blue-200 mt-4">{renderContent(currentQuiz.feedback)}</div>}
                    <button onClick={nextQuiz} className="w-full py-4 mt-2 rounded-2xl font-bold text-cyan-700 dark:text-white bg-cyan-500/20 border border-cyan-500/50 hover:bg-cyan-500/30 transition-all">{currentQuizIndex + 1 < sessionQuizzes.length ? "Question suivante ➔" : (preview ? "Recommencer ↺" : "Terminer la session")}</button>
                    {!preview && (!isReporting ? <button onClick={() => setIsReporting(true)} className="text-sm text-gray-600 dark:text-gray-500 hover:text-red-400 transition-colors w-full py-2 mt-4">🚩 Je ne suis pas d'accord / Signaler une erreur</button> : (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-2xl flex flex-col gap-3 mt-4"><textarea value={reportText} onChange={(e) => setReportText(e.target.value)} placeholder="Expliquez l'erreur..." className="w-full bg-gray-100 dark:bg-black/40 border border-red-500/30 rounded-xl p-3 text-red-700 dark:text-red-200 outline-none" rows="2" /><div className="flex gap-2 justify-end"><button onClick={() => setIsReporting(false)} className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-gray-400">Annuler</button><button onClick={submitReport} className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-xl text-xs font-bold">Envoyer le signalement</button></div></div>
                    ))}
                </div>
            )}
            {notification && <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-xl font-bold text-white shadow-lg transition-all z-50 ${notification.type === 'success' ? 'bg-emerald-500/90 border border-emerald-400' : 'bg-red-500/90 border border-red-400'}`}>{notification.text}</div>}
        </div>
    );
}

export default QuizSession;